
import { cn } from "@/lib/utils";
import { useNavigate, useLocation } from "react-router-dom";
import { SidebarLinkProps } from "./types";
import { memo, useCallback } from "react";

// Memoize the SidebarLink component to prevent unnecessary re-renders
export const SidebarLink = memo(({ item, collapsed, isActive: propIsActive }: SidebarLinkProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  
  // Enhanced check for active state
  const isActive = propIsActive || pathname === item.href;
  
  // Memoize the click handler to prevent re-renders
  const handleClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (item.href) {
      // Use React Router's navigate with replace option to prevent adding to history stack
      navigate(item.href, { replace: false });
    }
  }, [item.href, navigate]);

  return (
    <a
      href={item.href}
      onClick={handleClick}
      className={cn(
        "flex items-center gap-3 rounded-xl px-3 py-2.5 text-white/80 transition-all duration-300",
        "sidebar-item-hover relative overflow-hidden",
        "border border-transparent hover:scale-[1.02] active:scale-[0.98]",
        isActive ? "sidebar-item-active text-white" : "text-white/70",
        collapsed ? "justify-center" : ""
      )}
    >
      {/* Improved solid background on hover instead of transparent gradient */}
      <div className={cn(
        "absolute inset-0 opacity-0 transition-opacity duration-300",
        "bg-white/5", // Simplified background with solid color
        "hover:opacity-100",
        isActive && "opacity-100"
      )} />
      
      {/* Icon with enhanced effect */}
      <div className="sidebar-icon">
        <item.icon className={cn(
          "h-5 w-5 animate-float-subtle",
          isActive ? "text-neon-purple" : "text-white/70 group-hover:text-white/90"
        )} />
        <div className={cn(
          "sidebar-icon-glow",
          isActive && "opacity-50 bg-neon-purple/40"
        )}></div>
      </div>
      
      {/* Menu text with consistent font */}
      {!collapsed && (
        <span className={cn(
          "text-sm font-medium transition-all duration-300 whitespace-nowrap font-body tracking-wide",
          isActive ? "text-white font-semibold" : "text-white/70 group-hover:text-white"
        )}>
          {item.title}
        </span>
      )}
      
      {/* Improved active indicator with crisp styling */}
      {isActive && (
        <>
          {/* Left accent border */}
          <div className="absolute left-0 top-0 h-full w-[3px] bg-neon-purple"></div>
          
          {/* Solid highlight for active state instead of gradient */}
          <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-neon-purple/5"></div>
          
          {/* Bottom subtle line */}
          <div className="absolute left-4 right-4 bottom-0 h-[1px] bg-neon-purple/30"></div>
        </>
      )}
    </a>
  );
});

SidebarLink.displayName = 'SidebarLink';
